import React, { useState, useEffect } from 'react';
import { X, FolderOpen, FileText } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

const PERMISSION_OPTIONS = [
  { value: 'no_access', label: 'No Access', color: 'bg-gray-100 text-gray-700' },
  { value: 'view_only', label: 'View Only', color: 'bg-blue-50 text-blue-700' },
  { value: 'view_print', label: 'View + Print', color: 'bg-cyan-50 text-cyan-700' },
  { value: 'view_download', label: 'View + Download', color: 'bg-indigo-50 text-indigo-700' },
  { value: 'view_download_print', label: 'View + Download + Print', color: 'bg-emerald-50 text-emerald-700' },
  { value: 'redacted', label: 'Redacted', color: 'bg-amber-50 text-amber-700' },
];

export default function PermissionsMatrixModal({ open, onClose, dataRoomId }) {
  const [tab, setTab] = useState('folders'); // 'folders' | 'files'
  const [folderMatrix, setFolderMatrix] = useState(null);
  const [fileMatrix, setFileMatrix] = useState(null);
  const [folderFilter, setFolderFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTab('folders');
      loadFolderMatrix();
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (open && tab === 'files') loadFileMatrix(folderFilter);
  }, [open, tab, folderFilter]); // eslint-disable-line

  const loadFolderMatrix = async () => {
    setLoading(true);
    try {
      const res = await dataRoomAPI.getMatrix(dataRoomId);
      setFolderMatrix(res.data);
    } finally {
      setLoading(false);
    }
  };

  const loadFileMatrix = async (fid) => {
    setLoading(true);
    try {
      const res = await dataRoomAPI.getFileMatrix(dataRoomId, fid || null);
      setFileMatrix(res.data);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const handleFolderChange = async (userId, folderId, permission) => {
    try {
      await dataRoomAPI.setPermission(dataRoomId, {
        subject_type: 'user',
        subject_id: userId,
        resource_type: 'folder',
        resource_id: folderId,
        permission,
      });
      loadFolderMatrix();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleFileChange = async (userId, fileId, permission) => {
    try {
      await dataRoomAPI.setPermission(dataRoomId, {
        subject_type: 'user',
        subject_id: userId,
        resource_type: 'file',
        resource_id: fileId,
        permission,
      });
      loadFileMatrix(folderFilter);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const roots = (folderMatrix?.folders || [])
    .filter(f => !f.parent_id)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="matrix-modal">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Permissions Matrix</h2>
            <p className="text-xs text-gray-500">Users × Resources — effective access levels</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="close-matrix-modal">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b flex items-center gap-1 bg-gray-50">
          <button
            onClick={() => setTab('folders')}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
              tab === 'folders'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid="tab-folders"
          >
            <FolderOpen size={13} /> Folders
          </button>
          <button
            onClick={() => setTab('files')}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
              tab === 'files'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid="tab-files"
          >
            <FileText size={13} /> Files
          </button>

          {tab === 'files' && (
            <div className="ml-auto flex items-center gap-2 pb-1">
              <label className="text-[11px] text-gray-500">Scope:</label>
              <select
                value={folderFilter}
                onChange={(e) => setFolderFilter(e.target.value)}
                className="text-[11px] border rounded px-2 py-1 bg-white"
                data-testid="file-matrix-folder-filter"
              >
                <option value="">All folders</option>
                <option value="root">Root (no folder)</option>
                {(folderMatrix?.folders || []).map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading...</div>
          ) : tab === 'folders' ? (
            !folderMatrix || folderMatrix.users.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No external users invited yet.</div>
            ) : (
              <table className="w-full text-xs border-collapse" data-testid="matrix-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white border-r border-b text-left px-3 py-2 font-medium text-gray-700 min-w-[200px] z-10">User</th>
                    <th className="border-b text-left px-3 py-2 font-medium text-gray-700 min-w-[100px]">Role</th>
                    {roots.map(f => (
                      <th key={f.id} className="border-b text-left px-2 py-2 font-medium text-gray-700 min-w-[150px]">
                        {f.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {folderMatrix.users.map(u => (
                    <tr key={u.user_id} className="hover:bg-gray-50">
                      <td className="sticky left-0 bg-white border-r border-b px-3 py-2">
                        <div className="font-medium text-gray-900">{u.user_name}</div>
                        <div className="text-[10px] text-gray-500">{u.user_email}</div>
                        {u.is_revoked && (
                          <span className="text-[10px] text-red-600 font-medium">REVOKED</span>
                        )}
                      </td>
                      <td className="border-b px-3 py-2 text-gray-600">{u.role_name || '--'}</td>
                      {roots.map(f => {
                        const perm = u.folder_permissions[f.id] || 'no_access';
                        return (
                          <td key={f.id} className="border-b px-2 py-2">
                            <select
                              value={perm}
                              onChange={e => handleFolderChange(u.user_id, f.id, e.target.value)}
                              disabled={u.is_revoked}
                              className="w-full border rounded px-1 py-0.5 text-[11px] bg-white"
                              data-testid={`matrix-${u.user_id}-${f.id}`}
                            >
                              {PERMISSION_OPTIONS.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            // FILE MATRIX TAB
            !fileMatrix || fileMatrix.users.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No external users invited yet.</div>
            ) : fileMatrix.files.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No files uploaded yet in this scope.</div>
            ) : (
              <table className="w-full text-xs border-collapse" data-testid="file-matrix-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white border-r border-b text-left px-3 py-2 font-medium text-gray-700 min-w-[200px] z-10">User</th>
                    <th className="border-b text-left px-3 py-2 font-medium text-gray-700 min-w-[100px]">Role</th>
                    {fileMatrix.files.map(f => (
                      <th key={f.id} className="border-b text-left px-2 py-2 font-medium text-gray-700 min-w-[180px] max-w-[220px]">
                        <div className="truncate" title={f.name}>
                          {f.is_high_value && <span className="text-amber-500 mr-1">★</span>}
                          {f.name}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fileMatrix.users.map(u => (
                    <tr key={u.user_id} className="hover:bg-gray-50">
                      <td className="sticky left-0 bg-white border-r border-b px-3 py-2">
                        <div className="font-medium text-gray-900">{u.user_name}</div>
                        <div className="text-[10px] text-gray-500">{u.user_email}</div>
                        {u.is_revoked && (
                          <span className="text-[10px] text-red-600 font-medium">REVOKED</span>
                        )}
                      </td>
                      <td className="border-b px-3 py-2 text-gray-600">{u.role_name || '--'}</td>
                      {fileMatrix.files.map(f => {
                        const perm = u.file_permissions[f.id] || 'no_access';
                        return (
                          <td key={f.id} className="border-b px-2 py-2">
                            <select
                              value={perm}
                              onChange={e => handleFileChange(u.user_id, f.id, e.target.value)}
                              disabled={u.is_revoked}
                              className="w-full border rounded px-1 py-0.5 text-[11px] bg-white"
                              data-testid={`file-matrix-${u.user_id}-${f.id}`}
                            >
                              {PERMISSION_OPTIONS.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
          <div className="flex flex-wrap gap-2 text-[11px]">
            {PERMISSION_OPTIONS.map(p => (
              <span key={p.value} className={`px-2 py-0.5 rounded ${p.color}`}>{p.label}</span>
            ))}
          </div>
          <div className="text-[10px] text-gray-500 italic">
            {tab === 'files' ? 'File-level overrides trump folder-level settings.' : 'Folder-level permissions cascade to contained files.'}
          </div>
        </div>
      </div>
    </div>
  );
}
